import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { admin: "System Administrator", manager: "Finance Manager", staff: "Staff" };

export default function Users() {
  const [users, setUsers] = useState(null);
  const [sites, setSites] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "staff", assigned_sites: [], active: true });

  const load = () => { setUsers(null); api.get("/users").then((r) => setUsers(r.data)); };
  useEffect(() => { load(); api.get("/sites").then((r) => setSites(r.data)); }, []);

  const openNew = () => { setEditing(null); setF({ name: "", email: "", password: "", role: "staff", assigned_sites: [], active: true }); setDlg(true); };
  const openEdit = (u) => { setEditing(u); setF({ name: u.name, email: u.email, password: "", role: u.role, assigned_sites: u.assigned_sites || [], active: u.active }); setDlg(true); };

  const toggleSite = (id) => setF((p) => ({ ...p, assigned_sites: p.assigned_sites.includes(id) ? p.assigned_sites.filter((x) => x !== id) : [...p.assigned_sites, id] }));

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: f.name, role: f.role, assigned_sites: f.assigned_sites, active: f.active };
        if (f.password) payload.password = f.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        if (!f.email || !f.password || !f.name) { toast.error("Name, email and password are required"); return; }
        await api.post("/users", f);
      }
      toast.success("User saved"); setDlg(false); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div><h1 className="font-head text-2xl lg:text-3xl font-semibold text-slate-900">Users & Roles</h1><p className="text-slate-500 text-sm mt-1">Manage staff, managers and administrators.</p></div>
        <Button data-testid="add-user-btn" onClick={openNew} className="bg-[#0F172A] hover:bg-[#1E293B]"><Plus className="h-4 w-4 mr-1.5" /> Add User</Button>
      </div>

      <Card className="border-border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Homes</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {users === null && <TableRow><TableCell colSpan={6} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></TableCell></TableRow>}
              {users && users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium text-slate-800">{u.name}</TableCell>
                  <TableCell className="text-slate-600">{u.email}</TableCell>
                  <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{ROLE_LABEL[u.role]}</span></TableCell>
                  <TableCell className="text-slate-500 text-sm">{u.assigned_sites?.length || 0}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${u.active ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{u.active ? "Active" : "Inactive"}</span></TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" data-testid={`edit-user-${u.email}`} onClick={() => openEdit(u)}><Pencil className="h-3.5 w-3.5" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="user-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{editing ? "Edit User" : "Add User"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="user-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input data-testid="user-email" type="email" disabled={!!editing} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="mt-1.5" /></div>
            <div><Label>{editing ? "New password (leave blank to keep)" : "Password"}</Label><Input data-testid="user-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="user-role" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="staff">Staff</SelectItem><SelectItem value="manager">Finance Manager</SelectItem><SelectItem value="admin">System Administrator</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assigned homes</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                {sites.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={f.assigned_sites.includes(s.id)} onCheckedChange={() => toggleSite(s.id)} data-testid={`assign-${s.code}`} /> {s.name}
                  </label>
                ))}
              </div>
            </div>
            {editing && <div className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="user-active" /> <Label>Active</Label></div>}
          </div>
          <DialogFooter><Button data-testid="save-user" onClick={save} className="bg-[#0F172A] hover:bg-[#1E293B]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
